import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useAuditorStore } from '@/stores/auditorStore';
import { apiClient } from '@/services/api';

interface InvitationDetails {
  email: string;
  business_name: string;
  invited_by: string;
  expires_at: string;
}

export const AuditorInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser, user, isAuthenticated } = useAuthStore();
  const { setAccessibleBusinesses, setIsAuditor, setCurrentBusiness } = useAuditorStore();

  const [invitationData, setInvitationData] = useState<InvitationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoAccepting, setAutoAccepting] = useState(false);

  // Form state
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const token = searchParams.get('token');

  // Auto-accept invitation if user is already logged in as the same auditor
  const autoAcceptInvitation = async (invitationEmail: string) => {
    if (!token) return;

    setAutoAccepting(true);
    setError(null);

    try {
      const result = await apiClient.acceptInvitationExistingUser(token);

      if (result.success) {
        // Transform accessible_businesses to ensure merchant_id field exists
        const transformedBusinesses = (result.data.accessible_businesses || []).map((business: any) => ({
          ...business,
          merchant_id: business.merchant_id || business.id,
          name: business.business_name || business.name
        }));

        // Update accessible businesses in auditor store
        setAccessibleBusinesses(transformedBusinesses);

        // Refresh user data from server to ensure auth store is in sync
        try {
          const getCurrentUser = useAuthStore.getState().getCurrentUser;
          await getCurrentUser();
        } catch (err) {
          console.error('Failed to refresh user data after auto-accept:', err);
        }

        // Force a page reload to ensure all state is fresh
        window.location.href = '/';
      } else {
        setError('Failed to accept invitation');
        setAutoAccepting(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
      setAutoAccepting(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Fetch invitation details
    apiClient.getInvitationDetails(token)
      .then(async data => {
        if (data.success) {
          setInvitationData(data.data);

          // Check if user is already logged in as the same auditor
          // Verify with server to ensure token is still valid
          if (isAuthenticated && user && user.user_type === 'auditor') {
            try {
              // Verify current login state with server
              const currentUserResponse = await apiClient.getCurrentUser();

              if (currentUserResponse.success &&
                  currentUserResponse.data.user.user_type === 'auditor' &&
                  currentUserResponse.data.user.email === data.data.email) {
                // Auto-accept the invitation
                autoAcceptInvitation(data.data.email);
                return;
              }
            } catch (err) {
              // Token invalid or expired, show registration form
              console.log('Token validation failed, showing registration form');
            }
          }

          setLoading(false);
        } else {
          setError('Invalid or expired invitation');
          setLoading(false);
        }
      })
      .catch(() => {
        setError('Failed to load invitation details');
        setLoading(false);
      });
  }, [token, isAuthenticated, user]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!fullName || fullName.length < 2) {
      errors.full_name = 'Name must be at least 2 characters';
    }

    if (!password || password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (password !== confirmPassword) {
      errors.confirm_password = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !token) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await apiClient.acceptInvitation(token, fullName, password);

      if (result.success) {
        // Store auth data
        setToken(result.data.access_token);
        setUser({
          id: result.data.user_id,
          email: invitationData!.email,
          user_type: 'auditor',
        });

        // Transform accessible_businesses to ensure merchant_id field exists
        const transformedBusinesses = (result.data.accessible_businesses || []).map((business: any) => ({
          ...business,
          merchant_id: business.merchant_id || business.id,
          name: business.business_name || business.name
        }));

        // Store auditor data
        setIsAuditor(true);
        setAccessibleBusinesses(transformedBusinesses);

        // Set first business as current if available
        if (transformedBusinesses.length > 0) {
          setCurrentBusiness(transformedBusinesses[0]);
        }

        // Navigate to main dashboard (will show auditor view)
        navigate('/');
      } else {
        setError('Failed to accept invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || autoAccepting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">
            {autoAccepting ? 'Accepting invitation...' : 'Loading invitation...'}
          </p>
        </div>
      </div>
    );
  }

  if (error && !invitationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <CheckCircle className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Accept Auditor Invitation
          </h1>
        </div>

        {/* Invitation Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700 mb-2">
            You've been invited to access GST reports for:
          </p>
          <p className="font-semibold text-lg text-gray-900 mb-1">
            {invitationData?.business_name}
          </p>
          <p className="text-sm text-gray-600">
            Invited by: {invitationData?.invited_by}
          </p>
        </div>

        {/* Registration Form */}
        <form onSubmit={handleAccept} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="John Doe"
              required
            />
            {formErrors.full_name && (
              <p className="text-red-500 text-sm mt-1">{formErrors.full_name}</p>
            )}
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={invitationData?.email || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
            />
            <p className="text-xs text-gray-500 mt-1">This will be your login email</p>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
              required
            />
            {formErrors.password && (
              <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="••••••••"
              required
            />
            {formErrors.confirm_password && (
              <p className="text-red-500 text-sm mt-1">{formErrors.confirm_password}</p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Accepting...
              </>
            ) : (
              'Accept Invitation'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            By accepting, you agree to access this business's data in read-only mode
          </p>
        </div>
      </div>
    </div>
  );
};
